import {ChangeEventHandler, ComponentType, FC, ReactNode} from 'react'
import {Dropdown, Form} from 'react-bootstrap'
import {ThreeDotsVertical} from 'react-bootstrap-icons'
import styles from './AdminDropdown.module.css'

type IconComponent = ComponentType<{size?: number; className?: string}>

interface AdminDropdownProps {
    /** Trigger label/content (button variant). Ignored for the kebab variant. */
    toggle?: ReactNode
    /** Optional leading icon on a button-variant trigger. */
    icon?: IconComponent
    /** 'button' → link-style trigger; 'kebab' → ⋮ icon trigger;
     *  'custom' → caller-styled trigger via toggleClassName (caret suppressed). */
    variant?: 'button' | 'kebab' | 'custom'
    /** Enables the single-select accent-bar treatment on the active/hovered row. */
    selectable?: boolean
    /** Danger styling on the trigger button. */
    danger?: boolean
    align?: 'start' | 'end'
    /** Direction the menu opens (react-bootstrap pass-through); e.g. 'up' for bottom toolbars. */
    drop?: 'up' | 'down' | 'start' | 'end'
    disabled?: boolean
    title?: string
    ariaLabel?: string
    /** Extra class(es) on the trigger button (e.g. 'text-start' for wide toggles). */
    toggleClassName?: string
    /** Pass-through for react-bootstrap Dropdown auto-close behavior. */
    autoClose?: boolean | 'inside' | 'outside'
    /** Fixed popper strategy for menus rendered inside overflow/scroll containers. */
    popperFixed?: boolean
    /** Class on the Dropdown root (e.g. responsive 'd-grid d-md-block'). */
    className?: string
    /** Extra class on the menu (e.g. per-menu min-width override). */
    menuClassName?: string
    'data-testid'?: string
    children: ReactNode
}

const AdminDropdownBase: FC<AdminDropdownProps> = ({
    toggle,
    icon: Icon,
    variant = 'button',
    selectable = false,
    danger = false,
    align,
    drop,
    disabled,
    title,
    ariaLabel,
    toggleClassName,
    autoClose,
    popperFixed = false,
    className,
    menuClassName,
    'data-testid': dataTestId,
    children,
}) => {
    const popperProps = popperFixed
        ? {popperConfig: {strategy: 'fixed' as const}, renderOnMount: true, flip: false}
        : {}

    const menuClasses = [styles.menu, selectable ? styles.selectable : '', menuClassName ?? '']
        .filter(Boolean)
        .join(' ')

    return (
        <Dropdown align={align} drop={drop} className={className} autoClose={autoClose}>
            {variant === 'kebab' ? (
                <Dropdown.Toggle
                    as="button"
                    type="button"
                    className={styles.kebabToggle}
                    disabled={disabled}
                    title={title}
                    aria-label={ariaLabel}
                    data-testid={dataTestId}
                >
                    <ThreeDotsVertical size={18}/>
                </Dropdown.Toggle>
            ) : variant === 'custom' ? (
                <Dropdown.Toggle
                    as="button"
                    type="button"
                    className={`${styles.customToggle} ${toggleClassName ?? ''}`}
                    disabled={disabled}
                    title={title}
                    aria-label={ariaLabel}
                    data-testid={dataTestId}
                >
                    {Icon && <Icon size={14}/>}
                    {toggle}
                </Dropdown.Toggle>
            ) : (
                <Dropdown.Toggle
                    variant="link"
                    className={`text-decoration-none btn-link-action ${danger ? 'btn-link-action--danger' : ''} ${styles.toggle} ${toggleClassName ?? ''}`}
                    style={{color: 'inherit'}}
                    disabled={disabled}
                    title={title}
                    aria-label={ariaLabel}
                    data-testid={dataTestId}
                >
                    {Icon && <Icon size={14}/>}
                    {toggle}
                </Dropdown.Toggle>
            )}
            <Dropdown.Menu className={menuClasses} {...popperProps}>
                {children}
            </Dropdown.Menu>
        </Dropdown>
    )
}

interface AdminDropdownCheckboxProps {
    checked: boolean
    onChange: ChangeEventHandler<HTMLInputElement>
    label: ReactNode
    id?: string
    disabled?: boolean
}

interface AdminDropdownRadioProps extends AdminDropdownCheckboxProps {
    /** Groups the radios that belong to the same choice. */
    name: string
}

/** A checkbox row that toggles without closing the menu (column-picker menus). */
const AdminDropdownCheckbox: FC<AdminDropdownCheckboxProps> = ({checked, onChange, label, id, disabled}) => (
    <div className={styles.checkboxRow}>
        <Form.Check
            type="checkbox"
            id={id}
            label={label}
            checked={checked}
            disabled={disabled}
            onChange={onChange}
        />
    </div>
)

/** A radio row for an in-menu single choice; like Checkbox, it does not close the menu. */
const AdminDropdownRadio: FC<AdminDropdownRadioProps> = ({checked, onChange, label, id, disabled, name}) => (
    <div className={styles.checkboxRow}>
        <Form.Check
            type="radio"
            id={id}
            name={name}
            label={label}
            checked={checked}
            disabled={disabled}
            onChange={onChange}
        />
    </div>
)

/** A de-emphasized trailing "(n)" count badge; renders nothing when value is 0. */
const AdminDropdownCount: FC<{value: number}> = ({value}) =>
    value > 0 ? <span className={styles.count}>({value})</span> : null

const AdminDropdown = Object.assign(AdminDropdownBase, {
    Checkbox: AdminDropdownCheckbox,
    Radio: AdminDropdownRadio,
    Count: AdminDropdownCount,
})

export default AdminDropdown
